export const PERMISSION_DEFINITIONS = [
  {
    code: "admin.access",
    name: "Admin Access",
    module: "admin",
    description: "Open and use platform-wide admin tools.",
  },
  {
    code: "workspaces.manage",
    name: "Manage Workspaces",
    module: "workspace",
    description: "Create, update, and maintain workspace settings.",
  },
  {
    code: "screens.view",
    name: "View Screens",
    module: "screens",
    description: "See screen inventory and current device state.",
  },
  {
    code: "screens.create",
    name: "Create Screens",
    module: "screens",
    description: "Create and claim new screens.",
  },
  {
    code: "screens.edit",
    name: "Edit Screens",
    module: "screens",
    description: "Update screen details and assignments.",
  },
  {
    code: "screens.delete",
    name: "Delete Screens",
    module: "screens",
    description: "Remove screens from a workspace.",
  },
  {
    code: "screens.claim",
    name: "Claim Screens",
    module: "screens",
    description: "Activate devices by claim or registration flow.",
  },
  {
    code: "screens.assign",
    name: "Assign Screens",
    module: "screens",
    description: "Attach screens to playlists, schedules, or sequences.",
  },
  {
    code: "screens.activate",
    name: "Activate Screens",
    module: "screens",
    description: "Change device lifecycle and activation state.",
  },
  {
    code: "screens.zones.edit",
    name: "Edit Zones",
    module: "screens",
    description: "Configure templates, widgets, and zones.",
  },
  {
    code: "screens.player.preview",
    name: "Preview Player",
    module: "screens",
    description: "Preview the player output for a screen.",
  },
];

export const ROLE_DEFINITIONS = [
  {
    code: "user",
    name: "User",
    scope_type: "system",
    description: "Baseline global user with no platform-wide admin permissions.",
    permissions: [],
    assignable: false,
  },
  {
    code: "super_admin",
    name: "Super Admin",
    scope_type: "system",
    description: "Full platform control with every seeded permission.",
    permissions: PERMISSION_DEFINITIONS.map((permission) => permission.code),
  },
  {
    code: "admin",
    name: "Admin",
    scope_type: "system",
    description: "Platform administrator with access to the admin area.",
    permissions: ["admin.access"],
  },
  {
    code: "workspace_owner",
    name: "Workspace Owner",
    scope_type: "workspace",
    description: "Owns a workspace and can manage screens and workspace settings.",
    permissions: [
      "workspaces.manage",
      "screens.view",
      "screens.create",
      "screens.edit",
      "screens.delete",
      "screens.claim",
      "screens.assign",
      "screens.activate",
      "screens.zones.edit",
      "screens.player.preview",
    ],
  },
  {
    code: "workspace_admin",
    name: "Workspace Admin",
    scope_type: "workspace",
    description: "Administers a workspace without full platform access.",
    permissions: [
      "workspaces.manage",
      "screens.view",
      "screens.create",
      "screens.edit",
      "screens.delete",
      "screens.claim",
      "screens.assign",
      "screens.activate",
      "screens.zones.edit",
      "screens.player.preview",
    ],
  },
  {
    code: "screen_manager",
    name: "Screen Manager",
    scope_type: "workspace",
    description: "Manages the full screen lifecycle inside a workspace.",
    permissions: [
      "screens.view",
      "screens.create",
      "screens.edit",
      "screens.claim",
      "screens.assign",
      "screens.activate",
      "screens.zones.edit",
      "screens.player.preview",
    ],
  },
  {
    code: "screen_operator",
    name: "Screen Operator",
    scope_type: "workspace",
    description: "Operates assigned screens with limited setup powers.",
    permissions: [
      "screens.view",
      "screens.assign",
      "screens.activate",
      "screens.player.preview",
    ],
  },
  {
    code: "screen_viewer",
    name: "Screen Viewer",
    scope_type: "workspace",
    description: "Read-only access for monitoring screen playback.",
    permissions: [
      "screens.view",
      "screens.player.preview",
    ],
  },
];

export const permissionMap = PERMISSION_DEFINITIONS.reduce((acc, permission) => {
  acc[permission.code] = permission;
  return acc;
}, {});

export const roleMap = ROLE_DEFINITIONS.reduce((acc, role) => {
  acc[role.code] = role;
  return acc;
}, {});

export const SYSTEM_ROLE_OPTIONS = ROLE_DEFINITIONS.filter(
  (role) => role.scope_type === "system"
);

export const ASSIGNABLE_SYSTEM_ROLE_OPTIONS = SYSTEM_ROLE_OPTIONS.filter(
  (role) => role.assignable !== false
);

export const WORKSPACE_ROLE_OPTIONS = ROLE_DEFINITIONS.filter(
  (role) => role.scope_type === "workspace"
);

export const GLOBAL_PRIMARY_ROLE_OPTIONS = SYSTEM_ROLE_OPTIONS;

export const normalizeGlobalRole = (roleCode) => {
  const value = String(roleCode || "").trim().toLowerCase();
  if (value === "admin" || value === "super_admin") return value;
  return "user";
};

export const getRoleDefinition = (roleCode) =>
  roleMap[String(roleCode || "").trim().toLowerCase()] || null;

export const getPermissionDefinition = (permissionCode) =>
  permissionMap[String(permissionCode || "").trim().toLowerCase()] || null;

export const getPermissionsForRole = (roleCode) =>
  getRoleDefinition(roleCode)?.permissions || [];

export const getPermissionModules = (permissionCodes = []) => {
  const grouped = {};
  permissionCodes.forEach((code) => {
    const permission = getPermissionDefinition(code) || {
      code,
      name: code,
      module: "other",
      description: "",
    };
    const moduleKey = permission.module || "other";
    if (!grouped[moduleKey]) grouped[moduleKey] = [];
    grouped[moduleKey].push(permission);
  });

  return Object.entries(grouped)
    .map(([module, permissions]) => ({
      module,
      permissions: permissions.sort((left, right) =>
        left.name.localeCompare(right.name)
      ),
    }))
    .sort((left, right) => left.module.localeCompare(right.module));
};

export const getEffectivePermissionsFromAssignments = (assignments = []) => {
  const codes = new Set();
  assignments.forEach((assignment) => {
    getPermissionsForRole(
      assignment?.role_type || assignment?.role_code || assignment?.role
    ).forEach((permissionCode) => codes.add(permissionCode));
  });
  return Array.from(codes).sort((left, right) => left.localeCompare(right));
};

export const getRoleLabel = (roleCode) =>
  getRoleDefinition(roleCode)?.name ||
  String(roleCode || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

export const isWorkspaceScopedRole = (roleCode) =>
  getRoleDefinition(roleCode)?.scope_type === "workspace";
